<template>
  <div v-if="apiData">
    <Breadcrumbs v-if="apiData.breadcrumb.length" class="h-container mt-4 justify-center py-1.5 pb-4 text-sm" :elements="apiData.breadcrumb" />
    <ProductModal />
    <div class="md:h-gap h-container pb-10 md:grid md:grid-cols-12 md:pb-12">
      <div class="md:col-span-8">
        <ImageViewer :key="`image-viewer-${apiData.id}`" :images="apiData.images" :youtube-id="apiData.youtubeId" />
        <Button
          data-miros-entry="custom"
          :data-miros-item="apiData.id"
          class="btn-product-search-related btn-rounded invisible mx-auto mt-3 block [&.miros-supported]:visible"
          icon-name="miros-search"
        >
          <span class="mr-1 text-base">{{ t['w.module.product.use_wordless_search']() }}</span>
        </Button>
      </div>
      <div class="relative mt-8 md:col-span-4 md:mt-0 md:h-full">
        <div class="md:sticky md:top-24 md:transition-all md:duration-500">
          <div
            v-if="apiData.promotionalLabel || apiData.top100 || apiData.new || apiData.eligibleForFastShipping"
            class="relative mb-2 flex w-full flex-wrap justify-start gap-x-0.5"
          >
            <GiftLabel v-if="apiData.promotionalLabel" :text="apiData.promotionalLabel" />
            <Top100Label v-if="apiData.top100" />
            <NewLabel v-if="apiData.new" />
            <FastShippingLabel v-if="apiData.eligibleForFastShipping" />
          </div>

          <div class="pb-3">
            <h3 class="text-h3">{{ apiData.title }}</h3>
            <button v-show="isEmployeeMode" class="mt-3 cursor-pointer" :class="{ 'text-text-strong': copied }" @click="copy(`${apiData.id}`)">{{ productCode }}</button>
            <!--Rating class="mt-5 w-fit cursor-pointer" :value="productRating.amount" :text="`(${productRating.commentsCount})`" disabled @click="scrollToReviewsSection()" /-->
          </div>

          <!-- TODO Develop feature support for product campaign timers -->
          <!--Alert v-if="productCampaign && isCampaignActive" :text="productCampaign.text" /-->
          <!--Countdown v-if="productCampaign" class="pb-3" :target="productCampaign.target" :hours-limit="48" @campaign-visibility="isTargetCounting($event)" /-->

          <div v-if="apiData.forSale" class="border-t border-border py-5">
            <Prices v-bind="prices" />
            <p v-if="apiData.campaignText" class="mt-5 border-t border-border pt-5 text-sm">
              {{ apiData.campaignText }}
            </p>
          </div>

          <div class="border-border md:border-t">
            <OptionsSection v-if="apiData.forSale" v-bind="options" :highlight-empty-options="showOptionsError" @selected-options="selectedOptionsEvent($event)" />
            <FauxSticky>
              <!--WarrantySection v-if="apiData.forSale" :data="productWarranty" /-->
              <SubmissionForm v-bind="submission" @error="onOptionsError" @options-selected="onOptionsSelected" />
            </FauxSticky>
            <!-- TODO the following text can be removed once we have e-mail reminder notification support -->
            <div v-if="!apiData.forSale" class="flex items-center py-5 text-base">
              <Icon name="headphones" class="mr-3 size-6" />
              <div class="highlighted-link" v-html="t['w.module.product.ask_details']({ toode: productCode })" />
            </div>
            <ReplacementProduct class="py-6" v-bind="apiData.replacementProduct" />
            <div v-if="!apiData.forSale && recommendedHall" class="border-t border-border py-6">
              <p class="text-subheading">{{ t['w.module.submission_form.similar_products']() }}</p>

              <RecommendedHall class="mt-6 flex text-base" :hall="recommendedHall" :index="0" :show-icon="false" />
            </div>
          </div>

          <DeliverySection v-if="apiData.forSale" v-bind="delivery" class="border-t border-border py-3" />

          <ValuePropSection v-if="valuePropSection && apiData.forSale" :data="valuePropSection" />

          <p v-if="apiData.monthlyPayment && apiData.forSale" class="border-t border-border pt-6 text-sm text-text-light">
            {{ t['w.module.product.monthly_pay.disclaimer']() }}
          </p>
        </div>
      </div>
    </div>

    <ProductDescription
      :texts="apiData.texts"
      :title="apiData.title"
      :introduction="apiData.introduction"
      :brand="apiData.brand"
      :assembly-instructions-url="apiData.assemblyInstructionsUrl"
      :made-to-order="apiData.madeToOrder"
      :attributes="productAttributes"
      :recommended-halls="apiData.recommendedHalls"
    />

    <TiedProductsSection
      v-if="shouldShowTiedProductsSection"
      :family-products="apiData.familyProducts"
      :recommended-products="apiData.recommendedProducts"
      :title="apiData.title"
    />

    <CtaBanner />

    <!-- TODO: needs new visual design -->
    <!--div ref="reviewsSectionElement" class="h-container">
      <ReviewsSection
        :product-id="apiData.id"
        :reviews="reviewsSection?.reviews"
        :count="reviewsSection?.commentsCount"
        :comment-count-by-shop="reviewsSection?.commentCountByShop"
        :ratings="reviewsSection?.ratings"
      />
    </div-->
    <LastViewedProductsSection class="h-container flex flex-col items-center py-10" />
  </div>
</template>

<script setup lang="ts">
import Breadcrumbs from '~/components/breadcrumbs/Breadcrumbs.vue'
import ProductModal from '~/components/product-modal/ProductModal.vue'
import ImageViewer from '~/components/product/ImageViewer.vue'
import NewLabel from '~/components/product-grid/ProductLabelPillNew.vue'
import GiftLabel from '~/components/product-grid/ProductLabelPillGift.vue'
import Top100Label from '~/components/product-grid/ProductLabelPillTop100.vue'
import FastShippingLabel from '~/components/product-grid/ProductLabelPillShipping.vue'
import Prices from '~/components/product/Prices.vue'
import OptionsSection from '~/components/product/OptionsSection.vue'
import SubmissionForm from '~/components/product/SubmissionForm.vue'
import DeliverySection from '~/components/product/DeliverySection.vue'
import ProductDescription from '~/components/product/ProductDescription.vue'
import TiedProductsSection from '~/components/product/TiedProductsSection.vue'
import FauxSticky from '~/components/faux-sticky/FauxSticky.vue'
import CtaBanner from '~/components/cta-banner/CtaBanner.vue'
import ValuePropSection from '~/components/value-prop-section/ValuePropSection.vue'

import { useBreadcrumb } from '~/composables/structured-data'
import LastViewedProductsSection from '~/components/last-viewed-products-section/LastViewedProductsSection.vue'
import { useValuePropSectionsState } from '~/composables/states'
import { useLastViewedProducts } from '~/composables/last-viewed-products'
import ReplacementProduct from '~/components/replacement-product/ReplacementProduct.vue'
import RecommendedHall from '~/components/product/RecommendedHall.vue'
import { useEmployeeMode } from '~/composables/employee-mode'
import { useClipboard } from '@vueuse/core'
import { useProductQuery } from '~/composables/queries/product'
import { isSamePath } from 'ufo'
import * as t from '~/messages'
import Button from '~/components/button/Button.vue'
import { useMiros } from '~/composables/miros'

useMiros()

/* TODO: Develop feature support for product campaign timers
const isCampaignActive = ref()

const productCampaign = {
  text: 'Tootel on ajutine kampaaniahind.',
  target: '2023-06-21',
}

const isTargetCounting = (variable) => {
  isCampaignActive.value = variable
}
*/

/* TODO: Develop feature support for extra warranty
const productWarranty = Mock.getProductWarranty()
*/

/* TODO: Review section works, but needs correct visuals
const reviewsSectionElement = useTemplateRef('reviewsSectionElement')

const reviewsSection = ref(apiData.value.reviewsSection)

const productRating = computed(() => {
  return {
    amount: reviewsSection.value?.average,
    commentsCount: reviewsSection.value?.count,
    disabled: true,
    click: scrollToReviewsSection,
  }
})

const scrollToReviewsSection = () => {
  reviewsSectionElement.value?.scrollIntoView()
}
*/

const valuePropSection = useValuePropSectionsState()
const { isEmployeeMode } = useEmployeeMode()
const { copy, copied } = useClipboard()

const { proxy: gtmProxy } = useGtm()
const hasScrolledUp = ref(false)

const route = useRoute()
const productId = route.params.id as string
const hallId = computed(() => (typeof route.query.hall === 'string' ? route.query.hall : undefined))
const { data: apiData, suspense, error } = await useProductQuery(productId, hallId)
await suspense()
if (error.value) {
  throw createError(error.value)
}

if (!isSamePath(apiData.value.resourceUrl, useRoute().path)) {
  await navigateTo(
    {
      path: apiData.value.resourceUrl,
      query: route.query,
      hash: route.hash,
    },
    {
      replace: true,
    }
  )
}

let lastScrollY = 0

const updateScrollRefs = () => {
  hasScrolledUp.value = lastScrollY > window.scrollY
  lastScrollY = window.scrollY
}

provideTranslationUrl(() => apiData.value?.translationUrl)

const selectedOptions = ref(null)

const {
  public: { countryId, languageId },
} = useRuntimeConfig()

const productCode = computed(() => `${apiData.value.supplierPrefix}-${apiData.value.id}`)

const productCodeAttribute = {
  leftSide: t['w.product.product_code'](),
  rightSide: [productCode.value],
}

let productAttributes: { leftSide: string; rightSide: string[] }[] = apiData.value.attributes ?? []
productAttributes = productAttributes.concat(productCodeAttribute)

const showOptionsError = ref(false)

const onOptionsError = () => {
  showOptionsError.value = true
}

const onOptionsSelected = () => {
  showOptionsError.value = false
}

// Lowest price obtainable by anybody as a string in format '0.00'
const obtainablePrice = computed(() => {
  return ((apiData.value.discountPrice || apiData.value.regularPrice) / 100000).toFixed(2)
})

const shouldShowTiedProductsSection = computed(() => {
  return apiData.value.familyProducts?.length > 0 || apiData.value.recommendedProducts?.length > 0
})

const prices = computed(() => {
  return {
    regularPrice: apiData.value.regularPrice,
    discountPrice: apiData.value.discountPrice,
    virtualCreditPrice: apiData.value.virtualCreditPrice,
    monthlyPayment: apiData.value.monthlyPayment,
    discountPercentage: apiData.value.discountPercentage,
    virtualCreditDiscountPercentage: apiData.value.virtualCreditDiscountPercentage,
    countryId: countryId,
  }
})

const options = computed(() => {
  return {
    productId: apiData.value.id,
    groupMembers: apiData.value.groupMembers,
    optionGroups: apiData.value.optionGroups,
    variations: apiData.value.variations,
  }
})

const delivery = computed(() => {
  return {
    productCode: productCode.value,
    shouldShowDeliveryTimes: computedShouldShowDeliveryTimes.value,
    deliveryTimeTextForHome: computedDeliveryTimeTextForHome.value,
    deliveryTimeTextForParcelTerminal: computedDeliveryTimeTextForParcelTerminal.value,
    deliveryTimeTextForSelfPickup: computedDeliveryTimeTextForSelfPickup.value,
    shouldShowAskForMoreInfoHelper: computedShouldShowAskForMoreInfoHelper.value,
    fitsParcelTerminal: apiData.value.fitsParcelTerminal,
    selectedOptions: selectedOptions.value,
  }
})

const images = apiData.value.images.filter((image) => !image.dimensionsThumbnailUri)
const firstImageUrl = images[0] && (images[0].dUrl || images[0].cUrl)

const submission = computed(() => {
  return {
    id: apiData.value.id,
    url: apiData.value.canonicalUrl,
    forSale: apiData.value.forSale,
    selectedOptions: selectedOptions.value,
    selectedVariation: selectedVariation.value,
    optionGroups: apiData.value.optionGroups,
    productCode: productCode.value,
    productName: apiData.value.title,
    price: obtainablePrice.value,
    maxQuantity: apiData.value.maxQuantity,
    isError: showOptionsError.value,
    imageUrl: firstImageUrl,
    recommendedHalls: apiData.value.recommendedHalls,
    wishlisted: apiData.value.wishlisted,
  }
})

const computedDeliveryTimeTextForHome = computed(() => {
  return selectedVariation.value
    ? selectedVariation.value.deliveryTimeTextForHome
      ? selectedVariation.value.deliveryTimeTextForHome
      : null
    : apiData.value.deliveryTimeTextForHome
})

const computedDeliveryTimeTextForParcelTerminal = computed(() => {
  return selectedVariation.value
    ? selectedVariation.value.deliveryTimeTextForParcelTerminal
      ? selectedVariation.value.deliveryTimeTextForParcelTerminal
      : null
    : apiData.value.deliveryTimeTextForParcelTerminal
})

const computedDeliveryTimeTextForSelfPickup = computed(() => {
  return selectedVariation.value
    ? selectedVariation.value.deliveryTimeTextForSelfPickup
      ? selectedVariation.value.deliveryTimeTextForSelfPickup
      : null
    : apiData.value.deliveryTimeTextForSelfPickup
})

const computedShouldShowAskForMoreInfoHelper = computed(() => {
  return selectedVariation.value && selectedVariation.value.shouldShowAskForMoreInfoHelper
    ? selectedVariation.value.shouldShowAskForMoreInfoHelper
    : apiData.value.shouldShowAskForMoreInfoHelper
})

const computedShouldShowDeliveryTimes = computed(() => {
  // if we have option groups, we need to have a selected available variation (or option group is for legacy options - id 0)
  return !(apiData.value.optionGroups && apiData.value.variations) || !!selectedVariation.value || apiData.value.optionGroups[0]?.id === 0
})

const selectedOptionIds = computed(() => {
  return Object.values(selectedOptions.value || [])
    .filter((o) => o !== null)
    .map((o) => o.id)
})

const selectedVariation = computed(() => {
  if (!apiData.value.variations || !selectedOptions.value) {
    return null
  }

  const selectedOptionIdsText = [...selectedOptionIds.value].sort().join(',') // Avoid mutating stuff in computed

  return apiData.value.variations.find((v) => v.shippingTime !== 0 && v.optionIds.sort().join(',') === selectedOptionIdsText)
})

const recommendedHall = computed(() => {
  const breadcrumb = apiData.value.breadcrumb

  if (!breadcrumb) {
    return null
  }
  const breadcrumbLength = breadcrumb.length

  if (breadcrumbLength <= 1) {
    return null
  }
  const recommendedHall = breadcrumb[breadcrumbLength - 2]

  if (!recommendedHall.hall) {
    return null
  }

  return {
    id: 0,
    name: recommendedHall.name,
    filterNames: [],
    url: recommendedHall.url,
    params: [],
  }
})

watch(
  () => apiData.value.optionGroups,
  (newValue) => {
    if (!newValue) {
      // Navigating from optioned product to non-optioned does not reset this automatically as no new info comes in
      selectedOptions.value = null
    }
  }
)

const metaPixel = useMetaPixel()

const sendGtmProductView = () => {
  gtmProxy.dataLayer.push({
    event: 'fireRemarketingTag',
    google_tag_params: {
      dynx_itemid: `${languageId === 3 ? 'ru-' : ''}${apiData.value.id}`,
      dynx_pagetype: 'offerdetail',
      dynx_totalvalue: parseFloat(obtainablePrice.value),
      availability: apiData.value.forSale ? 'InStock' : 'OutOfStock',
    },
  })

  gtmProxy.dataLayer.push({
    event: 'ProductDetailView',
    ecommerce: {
      detail: {
        products: [
          {
            name: apiData.value.title,
            id: productCode.value,
            price: obtainablePrice.value,
            brand: (apiData.value.brand && apiData.value.brand.name) || '',
            category: '',
          },
        ],
      },
    },
  })
}

useHead({
  title: () => apiData.value?.pageTitle ?? null,
  link: () => [apiData.value?.canonicalUrl ? { rel: 'canonical', href: apiData.value.canonicalUrl } : undefined],
  meta: () => {
    const metaFields = []

    if (apiData.value?.metaDescription) {
      metaFields.push({ name: 'description', content: apiData.value.metaDescription })
    }

    if (apiData.value) {
      const images = apiData.value.images.filter((image) => !image.dimensionsThumbnailUri)
      const firstImageUrl = images[0] && (images[0].dUrl || images[0].cUrl)

      metaFields.push({ property: 'og:title', content: apiData.value.title })
      metaFields.push({ property: 'og:description', content: apiData.value.metaDescription })
      metaFields.push({ property: 'og:type', content: 'og:product' })
      metaFields.push({ property: 'og:image', content: firstImageUrl })
      metaFields.push({ property: 'og:site_name', content: 'ON24' })
      metaFields.push({ property: 'og:url', content: apiData.value.canonicalUrl })

      if (apiData.value.forSale) {
        metaFields.push({ property: 'og:price:amount', content: obtainablePrice.value })
        metaFields.push({ property: 'og:price:currency', content: useCurrencyCode() })
      }
    }

    return metaFields
  },
})

// TODO: #1 Strip HTML in backend
// TODO: #2 Report invalid use of `brand` to `nuxt-schema-org` or related project
// TODO: #3 Unwrap entire response from apiData.value?.title conditional
// TODO: #4 Consider refactoring into composable
if (apiData.value?.title) {
  // https://stackoverflow.com/a/47140708
  const strip = (html) => {
    return html

    //const doc = new DOMParser().parseFromString(html, 'text/html')

    //return doc.body.textContent || ''
  }

  //const brandName = apiData.value.brand && apiData.value.brand.name
  const images = apiData.value.images.filter((image) => !image.dimensionsThumbnailUri)
  const firstImageUrl = images[0] && (images[0].dUrl || images[0].cUrl)

  useSchemaOrg([
    defineBreadcrumb(useBreadcrumb(apiData.value?.breadcrumb)),
    /*defineOrganization({
      '@id': '#product-brand',
      name: brandName
    }),*/
    defineProduct({
      name: apiData.value.title,
      image: firstImageUrl,
      description: apiData.value.texts.map(strip).join(' '),
      sku: productCode.value,

      //  brand: '#product-brand',

      offers: {
        priceCurrency: useCurrencyCode(),
        price: obtainablePrice.value,
        availability: apiData.value.forSale ? 'InStock' : 'OutOfStock',
        url: apiData.value.canonicalUrl,
      },
    }),
  ])
}

const selectedOptionsEvent = (e) => {
  selectedOptions.value = e
}

const user = useUserState()

const { productImpression } = useCriteo()
const criteoProductEvent = () => {
  if (countryId !== 2) {
    return
  }

  const email = user ? user.emailMd5 : null
  const productId = apiData.value.id

  productImpression(email, productId)
}

onMounted(() => {
  const throttledUpdateScrollRefs = useThrottleFrame(updateScrollRefs)
  useEventListener(window, 'scroll', throttledUpdateScrollRefs, { passive: true })
  throttledUpdateScrollRefs()
  addToLastViewedProducts()
})

const { addToLastViewedProducts: addToLastViewedProductsInternal } = useLastViewedProducts()

const addToLastViewedProducts = () => {
  // TODO(Indrek): This is not correct and will lead to empty lists for products with single image, eg. 151467, 149943, 406854...
  // Temporarily guarded with empty array check, but this should be removed after investigation.
  const images = apiData.value.images.filter((image) => !image.dimensionsThumbnailUri)
  if (!images.length) {
    return
  }
  const image = images[0]
  const product = {
    id: apiData.value.id,
    title: apiData.value.title,
    url: apiData.value.resourceUrl,
    image: image.aUrl,
  }

  addToLastViewedProductsInternal(product)
}

sendGtmProductView()
criteoProductEvent()
metaPixel.sendProductView(`${apiData.value.id}`)
</script>

<style lang="scss" scoped>
.highlighted-link:deep(a) {
  @apply text-text-strong underline;

  &:hover {
    @apply no-underline;
  }
}
</style>
